import React from 'react';
import cs from 'classnames';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useThrottleCallback } from '@react-hook/throttle';
import { UiLoggerStore } from '../../../stores/UiLoggerStore';
import { isElementInView } from '../../../../utils/isElementInView';
import { injectV2 } from '../../../injectV2';
import { useOnWindowResize } from '../../../hooks/useOnWindowResize';
import { useOnWindowScroll } from '../../../hooks/useOnWindowScroll';
import { WidgetType } from '../../../stores/TemplatesStore';
import { widgetFeatureListConfig } from './widgetFeatureListConfig';
import { WidgetFeatureDetails } from './WidgetFeatureDetails/WidgetFeatureDetails';
import { WidgetFeature, WidgetFeatureList } from './WidgetFeatureList/WidgetFeatureList';
import { widgetDataHooks } from './Widget.dataHooks';
import styles from './Widget.scss';

interface WidgetProps {
  type: WidgetType | null;
  dataHook?: string;
  uiLoggerStore: UiLoggerStore;
}

const widgetTransitionEnterTimeout = parseInt(styles.widgetTransitionEnterTimeout, 10);
const widgetTransitionLeaveTimeout = parseInt(styles.widgetTransitionLeaveTimeout, 10);

const WidgetCmp: React.FC<WidgetProps> = ({ type, dataHook, uiLoggerStore }) => {
  const [isImpressionPending, setImpressionPending] = React.useState<boolean>(false);
  const [selectedFeature, selectFeature] = React.useState<WidgetFeature | null>(null);
  const [hoverStartTime, setHoverStartTime] = React.useState<number>(null);

  const widgetRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (isImpressionPending) {
      uiLoggerStore.logWidgetAboveTheFoldImpression();
    }
  }, [isImpressionPending, uiLoggerStore, uiLoggerStore.logWidgetAboveTheFoldImpression]);

  const reportIfVisibleToUser = React.useCallback(() => {
    if (!isImpressionPending && isElementInView(widgetRef.current)) {
      setImpressionPending(true);
    }
  }, [isImpressionPending]);

  React.useEffect(() => selectFeature(null), [type]);
  React.useEffect(() => {
    setImpressionPending(false);
  }, [type]);

  React.useEffect(() => {
    uiLoggerStore.logWidgetLoaded(type);
  }, [type, uiLoggerStore]);

  React.useEffect(() => {
    reportIfVisibleToUser();
  }, [reportIfVisibleToUser, type]);

  const throttleTimeout = 200;
  useOnWindowResize(useThrottleCallback(reportIfVisibleToUser, 1000 / throttleTimeout));
  useOnWindowScroll(useThrottleCallback(reportIfVisibleToUser, 1000 / throttleTimeout));

  const widgetFeatures = widgetFeatureListConfig[type];
  if (!widgetFeatures) {
    return null;
  }

  const { title, features } = widgetFeatures;

  const handleWidgetClick = () => uiLoggerStore.logWidgetActions('Thumbnail');
  const handleMouseLeave = () => {
    uiLoggerStore.logWidgetHover({
      widgetType: type,
      hoverTime: Date.now() - (hoverStartTime || 0),
    });
  };

  return (
    <li
      className={styles.container}
      data-hook={dataHook}
      ref={widgetRef}
      onMouseEnter={() => setHoverStartTime(Date.now())}
      onMouseLeave={handleMouseLeave}
    >
      <TransitionGroup>
        <CSSTransition
          classNames={{
            enter: styles.widgetEnter,
            enterActive: styles.widgetEnterActive,
            exit: styles.widgetLeave,
            exitActive: styles.widgetLeaveActive,
          }}
          timeout={{ enter: widgetTransitionEnterTimeout, exit: widgetTransitionLeaveTimeout }}
          key={selectedFeature ? 'details' : 'list'}
        >
          <div
            className={cs(styles.widget, {
              [styles.details]: selectedFeature,
              [styles.list]: !selectedFeature,
            })}
            onClick={handleWidgetClick}
            key={selectedFeature ? 'details' : 'list'}
          >
            <div className={styles.box}>
              <div className={styles.content}>
                {selectedFeature ? (
                  <WidgetFeatureDetails
                    dataHook={widgetDataHooks.details()}
                    features={features}
                    selectedFeature={selectedFeature}
                    selectFeature={selectFeature}
                  />
                ) : (
                  <WidgetFeatureList
                    dataHook={widgetDataHooks.list()}
                    title={title}
                    features={features}
                    onItemClick={selectFeature}
                  />
                )}
              </div>
            </div>
            <div className={styles.boxOverlay} />
          </div>
        </CSSTransition>
      </TransitionGroup>
    </li>
  );
};

export const Widget = injectV2('uiLoggerStore')(WidgetCmp);
